import { useMemo, useState } from "react";
import { X, MonitorPlay, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const NAME_REGEX = /^[A-Za-z ]*$/;
const ORG_REGEX = /^[A-Za-z\-&'. ]*$/;

const TIME_SLOTS = ["09:30", "11:00", "13:30", "15:00", "16:30", "17:30"];

const TIMEZONES = [
  { label: "Central European Time", value: "Europe/Berlin" },
  { label: "Eastern Time (US)", value: "America/New_York" },
  { label: "Pacific Time (US)", value: "America/Los_Angeles" },
  { label: "India Standard Time", value: "Asia/Kolkata" },
  { label: "Greenwich Mean Time", value: "Europe/London" },
];

function nextWeekdays(count) {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < count) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function fmtDay(d) {
  return {
    dow: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
    date: d.toLocaleDateString("en-US", { day: "numeric", month: "short" }),
    iso: d.toISOString().slice(0, 10),
  };
}

export default function DemoRequestModal({ onClose }) {
  const { requestDemo } = useAuth();
  const days = useMemo(() => nextWeekdays(4).map(fmtDay), []);

  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    org_name: "",
    role: "",
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timezone, setTimezone] = useState(TIMEZONES[0].value);

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};

    if (!form.full_name.trim()) e.full_name = "Full name is required.";
    if (!NAME_REGEX.test(form.full_name)) e.full_name = "Only letters and spaces allowed.";

    if (!form.org_name.trim()) e.org_name = "Organisation name is required.";
    if (!ORG_REGEX.test(form.org_name)) e.org_name = "Organisation name contains invalid characters.";

    if (!form.role.trim()) e.role = "Role is required.";

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";

    //if (!selectedDay || !selectedTime) e.slot = "Please pick a day and time.";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setBusy(true);
    try {
      await requestDemo({
        ...form,
        // requested_date: selectedDay,
        // requested_time: selectedTime,
        // timezone,

        requested_date: "",
        requested_time: "",
        timezone: "",
      });

      setSuccess(true);
      toast.success("Demo request received");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to request a demo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto py-8"
      style={{ background: "rgba(15,31,61,.45)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-[560px] rounded-2xl bg-white border border-[#E2E8F0] shadow-2xl p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X size={20} />
        </button>

        {!success ? (
          <>
            <div className="h-11 w-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-5">
              <MonitorPlay className="h-5 w-5 text-[#0F1F3D]" />
            </div>

            <h2 className="font-display text-2xl font-bold text-[#0F1F3D]">
              See how LaunchRadar works
            </h2>

            <p className="text-sm text-[#475569] mt-2">
              Twenty minutes on a live account. We walk you through how a project is set up, how every
              item gets scored against it, and what lands in the daily digest.
            </p>

            <form onSubmit={submit} className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                    Full name
                  </label>
                  <input
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Your Full Name"
                    className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                  />
                  {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                    Work email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@firm.com"
                    className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                    Organisation
                  </label>
                  <input
                    value={form.org_name}
                    onChange={update("org_name")}
                    placeholder="Firm or programme name"
                    className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                  />
                  {errors.org_name && <p className="text-xs text-red-500 mt-1">{errors.org_name}</p>}
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                    Your role
                  </label>
                  <input
                    value={form.role}
                    onChange={update("role")}
                    placeholder="Managing Partner"
                    className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                  />
                  {errors.role && <p className="text-xs text-red-500 mt-1">{errors.role}</p>}
                </div>
              </div>

              <hr className="border-[#F1F5F9]" />

              <div className= "hidden">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Pick a day
                </label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {days.map((d) => (
                    <button
                      key={d.iso}
                      type="button"
                      onClick={() => setSelectedDay(d.iso)}
                      className={`rounded-md border px-2 py-2.5 text-center transition-colors ${
                        selectedDay === d.iso
                          ? "border-[#0F1F3D] bg-[#0F1F3D] text-white"
                          : "border-[#E2E8F0] text-[#334155] hover:border-[#0F1F3D]"
                      }`}
                    >
                      <div className="text-[10px] font-bold tracking-wide opacity-70">{d.dow}</div>
                      <div className="text-sm font-bold">{d.date}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className= "hidden">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                    Pick a time
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="text-xs font-semibold text-[#334155] border border-[#E2E8F0] rounded-md px-2 py-1"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelectedTime(t)}
                      className={`rounded-md border px-2 py-2.5 text-center text-sm font-bold transition-colors ${
                        selectedTime === t
                          ? "border-[#0F1F3D] bg-[#0F1F3D] text-white"
                          : "border-[#E2E8F0] text-[#334155] hover:border-[#0F1F3D]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {errors.slot && <p className="text-xs text-red-500 mt-1">{errors.slot}</p>}
              </div>

              <button
                disabled={busy}
                className="w-full rounded-md bg-[#0F1F3D] py-2.5 text-white font-medium hover:bg-[#172A4F] disabled:opacity-50"
              >
                {busy ? "Sending..." : "Request a demo"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-md border border-[#E2E8F0] py-2.5"
              >
                Cancel
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="h-14 w-14 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
            </div>

            <h2 className="text-2xl font-bold text-[#0F1F3D]">Request received</h2>

            <p className="mt-3 text-sm text-[#475569]">
              We'll confirm your slot shortly. A calendar invite with the video link will land in your
              inbox.
            </p>

            <button onClick={onClose} className="mt-6 rounded-md bg-[#0F1F3D] px-6 py-2.5 text-white">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}